import { useEffect, useState } from "react";

import {
  Button,
  RadioButton,
  TextArea,
  Table,
  TableMetadata,
  Typography,
  useTable,
  Label,
} from "@king-ict/eupisi-ui/components";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { CommitteeDateRangePicker } from "./components";
import { DEFAULT_MINISTRY_COMMITTEE } from "./constants";
import {
  getMinistryCommitteeById,
  createMinistryCommittee,
  saveMinistryCommittee,
} from "../../api/Committee/MinistryCommitteeApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  FormCard,
  ToolbarEdit,
} from "../../components/";
import { useEnumCodeBooks } from "../../hooks";
import { ValidationFailure } from "../../models";
import {
  MinistryCommittee,
  CommitteeEmployee,
  OibEmployee,
  CommitteeType,
} from "../../models/Committee";
import { SaveResult } from "../../models/Shared";
import { ministryCommitteeDetailUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import { FormatUtils } from "../../utils";
import { useDeleteCommittee } from "./hooks/useDeleteCommittee";
import {
  generatingEditedEmployee,
  getCommitteeEmployeeDisplayName,
  isEmployeeActive,
  updateEmployeesWithPresident,
  replaceEmployeeInTableData,
} from "./utils";
import {
  setupCommitteeEmployees,
  updateCommitteeEmployee,
} from "./utils/committee.util";
import { CommitteeModal } from "./components/CommitteeModal";

export function MinistryCommitteeEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [item, setItem] = useState<MinistryCommittee>(
    DEFAULT_MINISTRY_COMMITTEE
  );
  const [tableData, setTableData] = useState<CommitteeEmployee[]>([]);
  const tableMetadata: TableMetadata<CommitteeEmployee> = {
    rowKey: "rowKeyId",
    columns: [
      {
        headerClassName: "col-4",
        columnKey: "expertCommissionRole",
        header: t("common.expertCommission.role"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeeRole?.name}</div>;
        },
      },
      {
        headerClassName: "col-5",
        columnKey: "employee",
        header: t("common.expertCommission.employee"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{getCommitteeEmployeeDisplayName(item)}</div>;
        },
      },
      {
        headerClassName: "col-4",
        columnKey: "activeTo",
        header: t("common.expertCommission.activeTo"),
        accessor: (item) => {
          return <div>{FormatUtils.formatDate(item.deactivationDate)}</div>;
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "edit",
        header: t("common.edit"),
        accessor: (employee) => {
          return (
            <div className={"text-right"}>
              <Button
                size="sm"
                outline
                onClick={() => handleDetailsClicked(employee)}
              >
                {t("common.edit")}
              </Button>
            </div>
          );
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "expertCommissionPosition",
        header: t("common.expertCommission.position"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeePosition?.name}</div>;
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "president",
        header: t("common.expertCommission.president"),
        accessor: (emp: CommitteeEmployee) => {
          const isActive = isEmployeeActive(emp);

          return (
            <div className="pt-2 ml-3">
              <RadioButton
                ariaLabel="item.president"
                key={emp.rowKeyId!}
                name={"item.president"}
                checked={emp.isPresident}
                onChange={() => handlePresidentChange(emp.rowKeyId!)}
                value={emp.rowKeyId!}
                disabled={!emp.employee || !isActive}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };

  const [tableProps] = useTable({
    metadata: tableMetadata,
    data: tableData,
    pagination: false,
    paginationPage: undefined,
    paginationPageSize: undefined,
    totalItems: 1,
    defaultSort: {},
  });

  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [editing, setEditing] = useState<CommitteeEmployee | null>(null);
  const { committeeStatusCodeBook: statuses } = useEnumCodeBooks();

  const { handleDeleteCommittee, isDeleteEnabled } = useDeleteCommittee({
    isEnabled: !item.hasDecisions,
    committeeType: "ministry",
    committeeId: +id,
  });

  useEffect(() => {
    (async () => {
      if (!isNaN(+id) && +id > 0) {
        const result = await getMinistryCommitteeById(+id);
        if (result) {
          setupCommitteeEmployees(result, setItem, setTableData);
        }
      } else {
        const result = await createMinistryCommittee();
        if (result) {
          setupCommitteeEmployees(result, setItem, setTableData);
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDetailsClicked = (
    expertCommissionEmployee: CommitteeEmployee
  ) => {
    setEditing(expertCommissionEmployee);
  };

  const handleDateChange = (date: Date, field: "dateFrom" | "dateTo") => {
    const newItem = { ...item };
    if (field === "dateFrom") {
      const dateTo =
        item.dateTo ||
        new Date(date.getFullYear() + 6, date.getMonth(), date.getDate());
      newItem.dateFrom = date;
      newItem.dateTo = dateTo;
    } else {
      newItem[field] = date;
    }

    setItem(newItem);
    dispatch(setIsEdited());
  };

  const handlePresidentChange = (rowKeyId: CommitteeEmployee["rowKeyId"]) => {
    const updatedEmployees = updateEmployeesWithPresident(tableData, rowKeyId);

    setTableData(updatedEmployees);
    setItem({
      ...item,
      employees: updatedEmployees,
    });
    dispatch(setIsEdited());
  };

  const handleNoteChange = (e: any) => {
    const note = e.target.value;
    const newItem: MinistryCommittee = {
      ...item,
      note: note,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };

  const handleOnSaveCommitteeEmployee = (
    newCommitteeEmployee: CommitteeEmployee
  ) => {
    if (!editing) return;

    const employeeToEdit = generatingEditedEmployee(
      editing,
      newCommitteeEmployee
    );

    setTableData((prev) =>
      replaceEmployeeInTableData(prev, employeeToEdit)
    );
    setItem((currItem) => updateCommitteeEmployee(currItem, employeeToEdit));
    setEditing(null);
  };

  const handleOnCloseModal = () => {
    setEditing(null);
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={"hide"}
        pageTitle={t("common.expertCommission.ministryPageNameEdit")}
        errors={errors}
      />

      <FormCard>
        <Row>
          <Col md={12}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.ministry")}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.dateFromDateTo")}
            </Typography>
          </Col>
          <Col md={4}>
            <Typography variant="base" color="light">
              {item.dateFrom ? hrDate(new Date(item.dateFrom)) : ""} -{" "}
              {item.dateTo ? hrDate(new Date(item.dateTo)) : ""}
            </Typography>
          </Col>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.status")}
            </Typography>
          </Col>
          <Col md={4}>
            <Label
              color={FormatUtils.getCommitteeStatusColor(item.statusId)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {statuses.find((v) => v.id === item.statusId)?.name}
            </Label>
          </Col>
        </Row>
      </FormCard>

      <FormCard>
        <Table {...tableProps} />
      </FormCard>

      <FormCard>
        <Row>
          <Col md={12}>
            <TextArea
              ariaLabel="item.note"
              label={t("common.expertCommission.note")}
              name={"item.note"}
              value={item.note ?? ""}
              onChange={handleNoteChange}
              errorText={
                errors?.find((e) => e.propertyName === "Note")?.message
              }
              maxLength={5000}
            />
          </Col>
        </Row>
        <Row>
          <CommitteeDateRangePicker
            dateFrom={item.dateFrom ? new Date(item.dateFrom) : null}
            dateTo={item.dateTo ? new Date(item.dateTo) : null}
            onDateFromChange={(date) => handleDateChange(date, "dateFrom")}
            onDateToChange={(date) => handleDateChange(date, "dateTo")}
            errors={errors}
          />
          <Col md={6}></Col>
        </Row>
      </FormCard>

      <ToolbarEdit
        showCancel={"show"}
        showSave={"show"}
        handleSave={async () => saveMinistryCommittee(item)}
        redirectUrlSaveOK={ministryCommitteeDetailUrl}
        setSaveErrors={setErrors}
        onSaveSuccess={(data: SaveResult<MinistryCommittee>) => {
          if (!data.result) return;

          setItem(data.result);
        }}
        showDelete={isDeleteEnabled ? "show" : "disable"}
        deleteDisabledReason={
          item.hasDecisions
            ? t("common.expertCommission.deleteDisabledHasDecisions")
            : undefined
        }
        onClickDelete={handleDeleteCommittee}
      />
      <CommitteeModal
        committeeType={CommitteeType.Ministry}
        editingEmployee={editing ?? null}
        onSave={handleOnSaveCommitteeEmployee}
        onClose={handleOnCloseModal}
      />
    </ContentArea>
  );
}
